import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Tabs, Tab, Card, CardContent, Button, IconButton,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
  Alert, Snackbar, Tooltip, CircularProgress, Grid, Avatar,
  Stack, InputAdornment, useMediaQuery, useTheme as useMuiTheme,
  Divider, Container, Pagination,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Dashboard as DashboardIcon,
  LocalHospital as LocalHospitalIcon,
  FitnessCenter as FitnessCenterIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon,
  TaskAlt as TaskAltIcon,
  Close as CloseIcon,
  MedicalServices as MedicalServicesIcon,
  CurrencyRupee as CurrencyRupeeIcon,
} from "@mui/icons-material";

// ─── Layout constants ─────────────────────────────────────────────────────────
const DRAWER_WIDTH = 280;
const MOBILE_BAR_H = 56;
const API          = process.env.REACT_APP_API;

// ─── Theme ────────────────────────────────────────────────────────────────────
export const adminTheme = createTheme({
  palette: {
    mode: "light",
    primary:    { main: "#006D77", light: "#00949f", dark: "#004d55", contrastText: "#fff" },
    secondary:  { main: "#E29578" },
    success:    { main: "#16a34a" },
    warning:    { main: "#d97706" },
    error:      { main: "#dc2626" },
    info:       { main: "#0284c7" },
    background: { default: "#F1F5F9", paper: "#ffffff" },
    text:       { primary: "#0f172a", secondary: "#64748b" },
  },
  typography: {
    fontFamily: "'DM Sans', 'Inter', sans-serif",
    h4: { fontFamily: "'Sora', sans-serif", fontWeight: 900 },
    h5: { fontFamily: "'Sora', sans-serif", fontWeight: 800 },
    h6: { fontFamily: "'Sora', sans-serif", fontWeight: 700 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: { boxShadow: "0 1px 4px rgba(0,0,0,0.07)", border: "1px solid #e2e8f0", borderRadius: 14 },
      },
    },
    MuiButton:    { styleOverrides: { root: { textTransform: "none", fontWeight: 700, borderRadius: 9 } } },
    MuiTab:       { styleOverrides: { root: { textTransform: "none", fontWeight: 600, minHeight: 44 } } },
    MuiChip:      { styleOverrides: { root: { fontWeight: 700 } } },
    MuiTableCell: {
      styleOverrides: {
        head: { backgroundColor: "#f8fafc", color: "#475569", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 },
        body: { fontSize: 13 },
      },
    },
    MuiTableRow:  { styleOverrides: { root: { "&:hover": { backgroundColor: "#f0fdfb" } } } },
    MuiTextField: { defaultProps: { size: "small" } },
    MuiSelect:    { defaultProps: { size: "small" } },
  },
});

// ─── Shared config ────────────────────────────────────────────────────────────
export const CAT_META = {
  nursing: {
    label: "Nursing", emoji: "🏥",
    gradient: "linear-gradient(135deg, #006D77 0%, #004E56 100%)",
    accent: "#006D77", lightBg: "#E0F7FA",
  },
  physiotherapy: {
    label: "Physiotherapy", emoji: "💪",
    gradient: "linear-gradient(135deg, #b8654a 0%, #8B3E2F 100%)",
    accent: "#b8654a", lightBg: "#FBE9E7",
  },
};

// ─── Auth helper ──────────────────────────────────────────────────────────────
export const authHeader = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Shared small components ──────────────────────────────────────────────────
export const CatChip = ({ value }) => {
  const m = CAT_META[value] || {};
  return (
    <Chip label={m.label || value} size="small"
      sx={{ bgcolor: m.lightBg, color: m.accent, fontWeight: 700, fontSize: 11, textTransform: "capitalize" }} />
  );
};

export const PaginationBar = ({ page, totalPages, onPageChange, total, pageSize, label = "items" }) => {
  if (totalPages <= 1) return null;
  const start = (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, total);
  return (
    <Box sx={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      px: { xs: 1.5, sm: 2.5 }, py: 1.5,
      borderTop: "1px solid #e2e8f0", bgcolor: "#fafafa",
      flexWrap: "wrap", gap: 1,
    }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        Showing {start}–{end} of {total} {label}
      </Typography>
      <Pagination page={page} count={totalPages} onChange={(_, v) => onPageChange(v)}
        size="small" color="primary" shape="rounded" siblingCount={1}
        sx={{
          "& .MuiPaginationItem-root": { fontWeight: 700, fontSize: 12 },
          "& .Mui-selected": { background: "linear-gradient(135deg, #006D77, #00949f) !important", color: "#fff" },
        }} />
    </Box>
  );
};

// ─── Service Mobile Card ──────────────────────────────────────────────────────
const ServiceMobileCard = ({ svc, onEdit, onDelete, onToggle }) => {
  const meta = CAT_META[svc.category] || CAT_META.nursing;
  return (
    <Card sx={{
      overflow: "hidden", mb: 1.5,
      bgcolor: svc.is_active ? "#fff" : "#FFF8F8",
      border: `1.5px solid ${svc.is_active ? "#e2e8f0" : "#FECACA"}`,
    }}>
      <Box sx={{ height: 4, background: meta.gradient }} />
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
              <Typography fontWeight={800} fontSize={14} sx={{ fontFamily: "'Sora',sans-serif" }}>
                {svc.name}
              </Typography>
              <CatChip value={svc.category} />
            </Box>
            {svc.description && (
              <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                {svc.description.slice(0, 80)}{svc.description.length > 80 ? "…" : ""}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                <CurrencyRupeeIcon sx={{ fontSize: 13, color: meta.accent }} />
                <Typography fontWeight={800} fontSize={14} sx={{ color: meta.accent }}>{svc.price}</Typography>
              </Box>
              {svc.price_b2b && (
                <Typography variant="caption" color="text.secondary">B2B: ₹{svc.price_b2b}</Typography>
              )}
              {svc.duration && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                  <AccessTimeIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary">{svc.duration}</Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Chip label={svc.is_active ? "Active" : "Inactive"} size="small"
            color={svc.is_active ? "success" : "default"} sx={{ fontSize: 10, flexShrink: 0 }} />
        </Box>

        {svc.conditions_covered?.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            {svc.conditions_covered.slice(0, 3).map((c, i) => (
              <Chip key={i} label={c} size="small" variant="outlined"
                sx={{ fontSize: 10, borderColor: alpha(meta.accent, 0.4), color: meta.accent }} />
            ))}
            {svc.conditions_covered.length > 3 && (
              <Chip label={`+${svc.conditions_covered.length - 3}`} size="small" sx={{ fontSize: 10 }} />
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1.5, borderTop: "1px solid #f1f5f9" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="caption" color="text.secondary">{svc.is_active ? "Active" : "Inactive"}</Typography>
            <Switch size="small" checked={!!svc.is_active} onChange={() => onToggle(svc._id)} color="success" />
          </Box>
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Button size="small" startIcon={<EditIcon sx={{ fontSize: 13 }} />} onClick={() => onEdit(svc)}
              sx={{ fontSize: 12, color: meta.accent, bgcolor: meta.lightBg, "&:hover": { bgcolor: alpha(meta.accent, 0.15) }, borderRadius: 1.5, px: 1.25 }}>
              Edit
            </Button>
            <Button size="small" startIcon={<DeleteIcon sx={{ fontSize: 13 }} />} onClick={() => onDelete(svc._id)}
              sx={{ fontSize: 12, color: "#DC2626", bgcolor: "#FEE2E2", "&:hover": { bgcolor: "#FECACA" }, borderRadius: 1.5, px: 1.25 }}>
              Delete
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Service Form Dialog ──────────────────────────────────────────────────────
const EMPTY_FORM = () => ({
  category: "nursing", name: "", description: "",
  price: "", price_b2b: "", duration: "",
  conditions_covered: "", is_active: true,
});

const ServiceFormDialog = ({ open, onClose, initial, onSave }) => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [form, setForm] = useState(EMPTY_FORM());

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        category: initial.category, name: initial.name,
        description: initial.description || "", price: initial.price,
        price_b2b: initial.price_b2b || "", duration: initial.duration || "",
        conditions_covered: (initial.conditions_covered || []).join(", "),
        is_active: initial.is_active,
      } : EMPTY_FORM());
    }
  }, [open, initial]);

  const set  = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const meta = CAT_META[form.category] || CAT_META.nursing;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, overflow: "hidden" } }}>

      <Box sx={{ background: meta.gradient, px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: 28, lineHeight: 1 }}>{meta.emoji}</Typography>
          <Box>
            <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, color: "#fff", fontSize: { xs: 15, sm: 17 } }}>
              {initial ? "Edit Service" : "Add New Service"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.65)", mt: 0.2 }}>
              {form.name || "Fill in the details below"}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"
          sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 3, px: { xs: 2, sm: 3 } }}>
        <Stack spacing={2.5}>
          <FormControl fullWidth>
            <InputLabel>Category *</InputLabel>
            <Select value={form.category} label="Category *" onChange={e => set("category", e.target.value)}>
              <MenuItem value="nursing">🏥 Nursing</MenuItem>
              <MenuItem value="physiotherapy">💪 Physiotherapy</MenuItem>
            </Select>
          </FormControl>

          <TextField label="Service Name *" fullWidth value={form.name}
            onChange={e => set("name", e.target.value)} />

          <TextField label="Description" fullWidth multiline rows={2}
            value={form.description} onChange={e => set("description", e.target.value)} />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Price (₹) *" fullWidth value={form.price}
              onChange={e => set("price", e.target.value)} placeholder="e.g. 800 or 800-1200"
              InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />
            <TextField label="B2B Price (₹)" fullWidth value={form.price_b2b}
              onChange={e => set("price_b2b", e.target.value)} placeholder="e.g. 650"
              InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />
          </Stack>

          <TextField label="Duration" fullWidth value={form.duration}
            onChange={e => set("duration", e.target.value)} placeholder="e.g. 30 mins, 1 hour"
            InputProps={{ startAdornment: <InputAdornment position="start"><AccessTimeIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />

          <TextField label="Conditions Covered (comma-separated)" fullWidth
            value={form.conditions_covered} onChange={e => set("conditions_covered", e.target.value)}
            placeholder="e.g. Neurological, Orthopedic, Cardiac"
            helperText="Separate each condition with a comma" />

          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={e => set("is_active", e.target.checked)} color="success" />}
            label={
              <Typography fontWeight={700} fontSize={14} color={form.is_active ? "#065F46" : "#991B1B"}>
                {form.is_active ? "✅ Active — visible to users" : "⛔ Inactive — hidden from users"}
              </Typography>
            }
          />
        </Stack>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)}
          sx={{ background: meta.gradient, px: 3, "&:hover": { background: meta.gradient, filter: "brightness(0.88)" } }}>
          {initial ? "Update Service" : "Create Service"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── MAIN EXPORT ─────────────────────────────────────────────────────────────
export default function NursingPhysioServicesAdmin() {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down("md"));

  const [catFilter, setCatFilter] = useState("all");
  const [search,    setSearch]    = useState("");
  const [services,  setServices]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [svcDlg,    setSvcDlg]    = useState({ open: false, data: null });
  const [delDlg,    setDelDlg]    = useState({ open: false, id: null, name: "" });
  const [snack,     setSnack]     = useState({ open: false, msg: "", sev: "success" });
  const [page,      setPage]      = useState(1);

  const notify = (msg, sev = "success") => setSnack({ open: true, msg, sev });
  const PAGE_SIZE = 8;

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/nursingphysiotherapy/admin/services`, { headers: authHeader() });
      const d = await r.json();
      if (d.success) setServices(d.services);
      else notify(d.error || "Failed to load services", "error");
    } catch { notify("Cannot reach server", "error"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);
  useEffect(() => { setPage(1); }, [catFilter, search]);

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const handleSaveService = async (form) => {
    if (!form.name.trim() || !form.price.trim()) { notify("Name and Price are required", "warning"); return; }
    const payload = {
      ...form,
      conditions_covered: form.conditions_covered
        ? form.conditions_covered.split(",").map(x => x.trim()).filter(Boolean)
        : [],
    };
    const isEdit = !!svcDlg.data;
    const url    = isEdit
      ? `${API}/nursingphysiotherapy/admin/services/${svcDlg.data._id}`
      : `${API}/nursingphysiotherapy/admin/services`;
    try {
      const r = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (d.success) {
        notify(isEdit ? "Service updated" : "Service created");
        setSvcDlg({ open: false, data: null });
        fetchServices();
      } else notify(d.error || "Save failed", "error");
    } catch { notify("Error saving service", "error"); }
  };

  const handleToggle = async (id) => {
    try {
      const r = await fetch(`${API}/nursingphysiotherapy/admin/services/${id}/toggle`, { method: "PATCH", headers: authHeader() });
      const d = await r.json();
      if (d.success) { notify(`Service ${d.is_active ? "activated" : "deactivated"}`); fetchServices(); }
    } catch { notify("Toggle failed", "error"); }
  };

  const handleDelete = async () => {
    try {
      const r = await fetch(`${API}/nursingphysiotherapy/admin/services/${delDlg.id}`, { method: "DELETE", headers: authHeader() });
      const d = await r.json();
      if (d.success) { notify("Service deleted", "warning"); fetchServices(); }
    } catch { notify("Delete failed", "error"); }
    setDelDlg({ open: false, id: null, name: "" });
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const filtered     = services.filter(s =>
    (catFilter === "all" || s.category === catFilter) &&
    s.name.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages   = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged        = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeCnt    = services.filter(s => s.is_active).length;

  const TH = { fontWeight: 700, fontSize: { xs: 11, sm: 12 }, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap" };
  const TD = { fontSize: { xs: 12, sm: 13 }, py: 1.25 };

  return (
    <ThemeProvider theme={adminTheme}>
      <link href="https://fonts.googleapis.com/css2?family=Sora:wght@700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <Box sx={{
        minHeight: "100vh",
        ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
        pt: { xs: `${MOBILE_BAR_H}px`, md: 0 },
        width: { xs: "100%", md: `calc(100% - ${DRAWER_WIDTH}px)` },
        boxSizing: "border-box", overflowX: "hidden",
      }}>
        {/* ── Sticky header ── */}
        <Box sx={{
          bgcolor: "white", borderBottom: "1px solid #e2e8f0",
          px: { xs: 2, sm: 3, md: 4 }, py: { xs: 1.5, sm: 2 },
          position: "sticky", top: { xs: `${MOBILE_BAR_H}px`, md: 0 },
          zIndex: 99, boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Box>
              <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: { xs: 15, sm: 19, md: 21 }, color: "#006D77" }}>
                🏥 Nursing & Physiotherapy — Services
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {activeCnt} active · {services.length - activeCnt} inactive · {services.length} total
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={fetchServices} sx={{ color: "#006D77" }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => setSvcDlg({ open: true, data: null })}
                size={isMobile ? "small" : "medium"}
                sx={{ background: "linear-gradient(135deg, #006D77, #00949f)", whiteSpace: "nowrap" }}>
                {isMobile ? "Add" : "Add Service"}
              </Button>
            </Stack>
          </Box>
        </Box>

        <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2.5, md: 4 }, py: { xs: 2, sm: 3 }, pb: 6 }}>
          {/* ── Filter bar ── */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} flexWrap="wrap">
                <TextField placeholder="Search services…" value={search} onChange={e => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} /></InputAdornment> }}
                  sx={{ flex: 1, minWidth: { xs: "100%", sm: 180 } }} />
                <FormControl sx={{ minWidth: { xs: "100%", sm: 160 } }}>
                  <InputLabel>Category</InputLabel>
                  <Select value={catFilter} label="Category" onChange={e => setCatFilter(e.target.value)}>
                    <MenuItem value="all">All Categories</MenuItem>
                    <MenuItem value="nursing">🏥 Nursing</MenuItem>
                    <MenuItem value="physiotherapy">💪 Physiotherapy</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}><CircularProgress sx={{ color: "#006D77" }} /></Box>
          ) : isTablet ? (
            /* ── Mobile cards ── */
            <Box>
              {paged.map(s => (
                <ServiceMobileCard key={s._id} svc={s}
                  onEdit={s => setSvcDlg({ open: true, data: s })}
                  onDelete={id => setDelDlg({ open: true, id, name: s.name })}
                  onToggle={handleToggle} />
              ))}
              {filtered.length === 0 && (
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                  <SearchIcon sx={{ fontSize: 40, opacity: 0.2 }} />
                  <Typography>No services found</Typography>
                </Box>
              )}
              <PaginationBar page={page} totalPages={totalPages} onPageChange={setPage}
                total={filtered.length} pageSize={PAGE_SIZE} label="services" />
            </Box>
          ) : (
            /* ── Desktop table ── */
            <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
              <Box sx={{ background: "linear-gradient(135deg, #003D45 0%, #006D77 100%)", px: 3, py: 2 }}>
                <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, color: "#fff", fontSize: 16 }}>
                  All Services
                </Typography>
                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
                  {activeCnt} active · {services.length - activeCnt} inactive
                </Typography>
              </Box>
              <TableContainer>
                <Table sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      {["#", "Service Name", "Category", "Price", "B2B", "Duration", "Conditions", "Status", "Actions"].map(h => (
                        <TableCell key={h} sx={{ ...TH }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((s, idx) => (
                      <TableRow key={s._id} sx={{ bgcolor: s.is_active ? "#fff" : "#FFF8F8" }}>
                        <TableCell sx={{ ...TD, color: "#CBD5E1", width: 32 }}>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                        <TableCell sx={{ ...TD }}>
                          <Typography fontWeight={700} fontSize={13}>{s.name}</Typography>
                          {s.description && (
                            <Typography variant="caption" color="text.secondary">
                              {s.description.slice(0, 50)}{s.description.length > 50 ? "…" : ""}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...TD }}><CatChip value={s.category} /></TableCell>
                        <TableCell sx={{ ...TD }}>
                          <Typography fontWeight={800} color="#006D77" fontSize={13}>₹{s.price}</Typography>
                        </TableCell>
                        <TableCell sx={{ ...TD }}>
                          {s.price_b2b
                            ? <Typography fontSize={12}>₹{s.price_b2b}</Typography>
                            : <Typography color="text.secondary" fontSize={12}>—</Typography>}
                        </TableCell>
                        <TableCell sx={{ ...TD }}>
                          {s.duration
                            ? <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                                <AccessTimeIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                                <Typography fontSize={12}>{s.duration}</Typography>
                              </Box>
                            : <Typography color="text.secondary" fontSize={12}>—</Typography>}
                        </TableCell>
                        <TableCell sx={{ ...TD, maxWidth: 160 }}>
                          {s.conditions_covered?.length > 0
                            ? <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                                {s.conditions_covered.slice(0, 2).map((c, i) => (
                                  <Chip key={i} label={c} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                                ))}
                                {s.conditions_covered.length > 2 && (
                                  <Chip label={`+${s.conditions_covered.length - 2}`} size="small" sx={{ fontSize: 10 }} />
                                )}
                              </Box>
                            : <Typography color="text.secondary" fontSize={12}>—</Typography>}
                        </TableCell>
                        <TableCell sx={{ ...TD }}>
                          <Chip label={s.is_active ? "Active" : "Inactive"} size="small" color={s.is_active ? "success" : "default"} />
                        </TableCell>
                        <TableCell sx={{ ...TD }}>
                          <Stack direction="row" spacing={0.3}>
                            <Tooltip title={s.is_active ? "Deactivate" : "Activate"}>
                              <IconButton size="small" color={s.is_active ? "success" : "default"} onClick={() => handleToggle(s._id)}>
                                {s.is_active ? <ToggleOnIcon /> : <ToggleOffIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small" color="primary" onClick={() => setSvcDlg({ open: true, data: s })}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setDelDlg({ open: true, id: s._id, name: s.name })}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 5, color: "text.secondary" }}>No services found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <PaginationBar page={page} totalPages={totalPages} onPageChange={setPage}
                total={filtered.length} pageSize={PAGE_SIZE} label="services" />
            </Paper>
          )}
        </Container>
      </Box>

      {/* ── Service form dialog ── */}
      <ServiceFormDialog
        open={svcDlg.open}
        onClose={() => setSvcDlg({ open: false, data: null })}
        initial={svcDlg.data}
        onSave={handleSaveService}
      />

      {/* ── Delete confirm ── */}
      <Dialog open={delDlg.open} onClose={() => setDelDlg({ open: false, id: null, name: "" })}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: 15, sm: 17 } }}>Delete Service?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Permanently delete <strong>"{delDlg.name}"</strong>? This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDelDlg({ open: false, id: null, name: "" })} color="inherit">Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} sx={{ fontWeight: 700 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} variant="filled"
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}